/* eslint-disable @typescript-eslint/no-explicit-any */
import { values } from 'rambda';
import { Directus_Files } from 'src/graphql/schema.graphql';
import type { BaseProductDTO } from 'src/models/pim';

export enum ProductAttributeCode {
  AestheticIndicationText = 'aesthetic-indication-text',
  AestheticIndicationTitle = 'aesthetic-indication-title',
  Application = 'application',
  ApplicationTime = 'application-time',
  AreaOfInjection = 'area-of-injection',
  BeautyRoutines = 'beauty-routines',
  BeautyTip = 'beauty-tip',
  BooleanAttribute = 'boolean-attribute',
  CrossSellingTitle = 'cross-selling-title',
  DateTimeAttribute = 'date-time-attribute',
  Duration = 'duration',
  ExtrusionForce = 'extrusion-force',
  FdaIcon = 'fda-icon',
  HaConcentration = 'ha-concentration',
  HowItWorksGroup = 'how-it-works-group',
  HowItWorksGroupImage = 'how-it-works-group-image',
  HowItWorksGroupText = 'how-it-works-group-text',
  HowItWorksGroupTitle = 'how-it-works-group-title',
  HowItWorksSubtitle = 'how-it-works-subtitle',
  HowItWorksText = 'how-it-works-text',
  Ingredients = 'ingredients',
  IngredientsEffectivenessText = 'ingredients-effectiveness-text',
  InjectionPlace = 'injection-place',
  InjectionTool = 'injection-tool',
  KeyBenefitsText = 'key-benefits-text',
  KeyBenefitsTitle = 'key-benefits-title',
  KeyIngredients = 'key-ingredients',
  Leaflet = 'leaflet',
  LegalMentions = 'legal-mentions',
  FeaturesImage = 'product-features-image',
  ProductsInvolvedGroup = 'products-involved-group',
  ProductsInvolvedGroupCode = 'products-involved-group-code',
  ProductsInvolvedGroupImage = 'products-involved-group-image',
  ProductsInvolvedGroupName = 'products-involved-group-name',
  ProductsInvolvedGroupProduct = 'products-involved-group-product',
  ProductsInvolvedGroupText = 'products-involved-group-text',
  ProductsInvolvedText = 'products-involved-text',
  ProductsInvolvedTitle = 'products-involved-title',
  ProtocolDescriptionText = 'protocol-description-text',
  ProtocolDescriptionTitle = 'protocol-description-title',
  ProtocolsSessionsGroup = 'protocols-sessions-group',
  ProtocolsSessionsGroupStepGroup = 'protocols-sessions-group-step-group',
  ProtocolsSessionsGroupStepGroupCode = 'protocols-sessions-group-step-group-code',
  ProtocolsSessionsGroupStepGroupProduct = 'protocols-sessions-group-step-group-product',
  ProtocolsSessionsGroupStepGroupText = 'protocols-sessions-group-step-group-text',
  ProtocolsSessionsGroupStepGroupTitle = 'protocols-sessions-group-step-group-title',
  ProtocolsSessionsGroupTitle = 'protocols-sessions-group-title',
  ProtocolsSessionsGroupText = 'protocols-sessions-group-text',
  ProtocolsSessionsText = 'protocols-sessions-text',
  ProtocolsSessionsTitle = 'protocols-sessions-title',
  ResultsText = 'results-text',
  SctLinkGroup = 'sct-link-group',
  SctLinkGroupCode = 'sct-link-group-code',
  SctLinkGroupImage = 'sct-link-group-image',
  SctLinkGroupName = 'sct-link-group-name',
  SctLinkGroupProduct = 'sct-link-group-product',
  SctLinkGroupText = 'sct-link-group-text',
  StiffnessLevel = 'stiffness-level',
  StiffnessText = 'stiffness-text',
  TechnicalFeaturesAndHandpieceGroup = 'technical-features-and-handpiece-group',
  TechnicalFeaturesAndHandpieceGroupImage = 'technical-features-and-handpiece-group-image',
  TechnicalFeaturesAndHandpieceGroupText = 'technical-features-and-handpiece-group-text',
  TechnicalFeaturesAndHandpieceGroupTitle = 'technical-features-and-handpiece-group-title',
  TechnicalFeaturesAndHandpieceText = 'technical-features-and-handpiece-text',
  TestFiles = 'test-files',
  TreatmentEssentials = 'treatment-essentials',
  TreatmentEssentialsText = 'treatment-essentials-text',
  Volume = 'volume',
}

export enum ProductAttributeCollection {
  TextValues = 'text_values',
  GroupValues = 'group_values',
  DictionaryValues = 'dictionary_values',
  StringValues = 'string_values',
  NumericValues = 'numeric_values',
  BooleanValues = 'boolean_values',
  DateTimeValues = 'date_time_values',
  ImagesValues = 'images_values',
  FilesValues = 'files_values',
  LinkedProductsValues = 'linked_products_values',
}

export type ProductTextValuesAttribute = {
  collection: ProductAttributeCollection.TextValues;
  item: {
    attribute: ProductAttributeCode;
    translations: {
      languages_code: string;
      value: string;
    }[];
  };
};

export type ProductDictionaryValuesAttribute = {
  collection: ProductAttributeCollection.DictionaryValues;
  item: {
    attribute: ProductAttributeCode;
    value: {
      dictionary_entries_code: {
        code: ProductAttributeCode;
        translations: {
          languages_code: string;
          value: string;
        }[];
      };
    }[];
  };
};

export type ProductStringValuesAttribute = {
  collection: ProductAttributeCollection.StringValues;
  item: {
    attribute: ProductAttributeCode;
    value: string;
  };
};

export type ProductNumericValuesAttribute = {
  collection: ProductAttributeCollection.NumericValues;
  item: {
    attribute: ProductAttributeCode;
    value: number;
  };
};

export type ProductBooleanValuesAttribute = {
  collection: ProductAttributeCollection.BooleanValues;
  item: {
    attribute: ProductAttributeCode;
    value: boolean;
  };
};

export type ProductDateTimeValuesAttribute = {
  collection: ProductAttributeCollection.DateTimeValues;
  item: {
    attribute: ProductAttributeCode;
    value: string;
  };
};

export type ProductImagesValuesAttribute = {
  collection: ProductAttributeCollection.ImagesValues;
  item: {
    attribute: ProductAttributeCode;
    value: {
      directus_files_id: Directus_Files;
    }[];
  };
};

export type ProductFilesValuesAttribute = {
  collection: ProductAttributeCollection.FilesValues;
  item: {
    attribute: ProductAttributeCode;
    value: {
      directus_files_id: Directus_Files;
    }[];
  };
};

export type ProductLinkedProductsValuesAttribute = {
  collection: ProductAttributeCollection.LinkedProductsValues;
  item: {
    attribute: ProductAttributeCode;
    value: {
      products_id: BaseProductDTO;
    }[];
  };
};

export type ProductGroupValuesAttribute = {
  collection: ProductAttributeCollection.GroupValues;
  item: {
    attribute: ProductAttributeCode;
    value: (
      | ProductTextValuesAttribute
      | ProductStringValuesAttribute
      | ProductDictionaryValuesAttribute
      | ProductNumericValuesAttribute
      | ProductBooleanValuesAttribute
      | ProductDateTimeValuesAttribute
      | ProductImagesValuesAttribute
      | ProductFilesValuesAttribute
      | ProductLinkedProductsValuesAttribute
    )[];
  };
};

export type ProductAttribute =
  | ProductTextValuesAttribute
  | ProductStringValuesAttribute
  | ProductDictionaryValuesAttribute
  | ProductNumericValuesAttribute
  | ProductBooleanValuesAttribute
  | ProductDateTimeValuesAttribute
  | ProductImagesValuesAttribute
  | ProductFilesValuesAttribute
  | ProductLinkedProductsValuesAttribute
  | ProductGroupValuesAttribute;

export type ProductDictionaryOrTextCollection =
  | ProductAttributeCollection.TextValues
  | ProductAttributeCollection.DictionaryValues;

type CollectionAttributeMap = {
  [ProductAttributeCollection.TextValues]: ProductTextValuesAttribute;
  [ProductAttributeCollection.GroupValues]: ProductGroupValuesAttribute;
  [ProductAttributeCollection.DictionaryValues]: ProductDictionaryValuesAttribute;
  [ProductAttributeCollection.StringValues]: ProductStringValuesAttribute;
  [ProductAttributeCollection.NumericValues]: ProductNumericValuesAttribute;
  [ProductAttributeCollection.BooleanValues]: ProductBooleanValuesAttribute;
  [ProductAttributeCollection.DateTimeValues]: ProductDateTimeValuesAttribute;
  [ProductAttributeCollection.ImagesValues]: ProductImagesValuesAttribute;
  [ProductAttributeCollection.FilesValues]: ProductFilesValuesAttribute;
  [ProductAttributeCollection.LinkedProductsValues]: ProductLinkedProductsValuesAttribute;
};

type CodeCollectionMap = {
  [ProductAttributeCode.AestheticIndicationText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.AestheticIndicationTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.Application]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ApplicationTime]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.AreaOfInjection]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.BeautyRoutines]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.BeautyTip]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.BooleanAttribute]: ProductAttributeCollection.BooleanValues;
  [ProductAttributeCode.CrossSellingTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.DateTimeAttribute]: ProductAttributeCollection.DateTimeValues;
  [ProductAttributeCode.Duration]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ExtrusionForce]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.FdaIcon]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.HaConcentration]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.HowItWorksGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.HowItWorksGroupImage]: ProductAttributeCollection.ImagesValues;
  [ProductAttributeCode.HowItWorksGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.HowItWorksGroupTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.HowItWorksSubtitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.HowItWorksText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.Ingredients]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.IngredientsEffectivenessText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.InjectionPlace]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.InjectionTool]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.KeyBenefitsText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.KeyBenefitsTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.KeyIngredients]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.Leaflet]: ProductAttributeCollection.FilesValues;
  [ProductAttributeCode.LegalMentions]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.FeaturesImage]: ProductAttributeCollection.ImagesValues;
  [ProductAttributeCode.ProductsInvolvedGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.ProductsInvolvedGroupCode]: ProductAttributeCollection.StringValues;
  [ProductAttributeCode.ProductsInvolvedGroupImage]: ProductAttributeCollection.ImagesValues;
  [ProductAttributeCode.ProductsInvolvedGroupName]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProductsInvolvedGroupProduct]: ProductAttributeCollection.LinkedProductsValues;
  [ProductAttributeCode.ProductsInvolvedGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProductsInvolvedText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProductsInvolvedTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolDescriptionText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolDescriptionTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.ProtocolsSessionsGroupStepGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.ProtocolsSessionsGroupStepGroupCode]: ProductAttributeCollection.StringValues;
  [ProductAttributeCode.ProtocolsSessionsGroupStepGroupProduct]: ProductAttributeCollection.LinkedProductsValues;
  [ProductAttributeCode.ProtocolsSessionsGroupStepGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsGroupStepGroupTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsGroupTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ProtocolsSessionsTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.ResultsText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.SctLinkGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.SctLinkGroupCode]: ProductAttributeCollection.StringValues;
  [ProductAttributeCode.SctLinkGroupImage]: ProductAttributeCollection.ImagesValues;
  [ProductAttributeCode.SctLinkGroupName]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.SctLinkGroupProduct]: ProductAttributeCollection.LinkedProductsValues;
  [ProductAttributeCode.SctLinkGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.StiffnessLevel]: ProductAttributeCollection.NumericValues;
  [ProductAttributeCode.StiffnessText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.TechnicalFeaturesAndHandpieceGroup]: ProductAttributeCollection.GroupValues;
  [ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupImage]: ProductAttributeCollection.ImagesValues;
  [ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.TechnicalFeaturesAndHandpieceGroupTitle]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.TechnicalFeaturesAndHandpieceText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.TestFiles]: ProductAttributeCollection.FilesValues;
  [ProductAttributeCode.TreatmentEssentials]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.TreatmentEssentialsText]: ProductDictionaryOrTextCollection;
  [ProductAttributeCode.Volume]: ProductDictionaryOrTextCollection;
};

const parseAttribute = <T extends ProductAttributeCollection>(
  attribute: ProductAttribute,
): CollectionAttributeMap[T]['item'] extends {
  value: infer U;
}
  ? U
  : T extends ProductDictionaryOrTextCollection
  ? (ProductTextValuesAttribute['item'] & { code?: string })[]
  : CollectionAttributeMap[T]['item'] => {
  const item = attribute.item as any;

  if ('value' in item) {
    const isDictionary = !!(
      Array.isArray(item.value) &&
      item.value.some((v: object) => 'dictionary_entries_code' in v)
    );

    if (isDictionary) {
      return item.value.map(
        (v: ProductDictionaryValuesAttribute['item']['value'][number]) =>
          v.dictionary_entries_code,
      );
    }

    return item.value;
  }

  return [item] as any;
};

const filterAttributesByCodeOrCollection = (
  attr: ProductAttribute,
  codeOrCollection: ProductAttributeCode | ProductAttributeCollection,
): boolean => {
  const {
    item: { attribute: code },
    collection,
  } = attr;

  const isCode = values(ProductAttributeCode).includes(
    codeOrCollection as ProductAttributeCode,
  );

  if (isCode) {
    return code === codeOrCollection;
  }

  return collection === codeOrCollection;
};

export const getProductAttribute = <
  T extends ProductAttributeCode | ProductAttributeCollection,
>(
  attributes: ProductAttribute[],
  codeOrCollection: T,
) => {
  const matchingAttribute = attributes?.find((a) =>
    filterAttributesByCodeOrCollection(a, codeOrCollection),
  );

  if (matchingAttribute) {
    return parseAttribute<
      T extends ProductAttributeCode ? CodeCollectionMap[T] : T
    >(matchingAttribute);
  }

  return null;
};

export const getMultipleProductAttributes = <
  T extends ProductAttributeCode | ProductAttributeCollection,
>(
  attributes: ProductAttribute[],
  codeOrCollection: T,
) =>
  attributes
    ?.filter((a) => filterAttributesByCodeOrCollection(a, codeOrCollection))
    .map((attribute) =>
      parseAttribute<T extends ProductAttributeCode ? CodeCollectionMap[T] : T>(
        attribute,
      ),
    ) ?? [];
